import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsAPI } from '../api/api';
import { trackConversion } from '../utils/analytics';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  XCircle, 
  Clock,
  ArrowRight
} from '@phosphor-icons/react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    let isMounted = true;
    
    const checkPaymentStatus = async () => {
      if (!isMounted) return;
      
      try {
        const response = await paymentsAPI.getStatus(sessionId);
        const data = response.data;
        
        if (!isMounted) return;
        
        if (data.payment_status === 'paid') {
          trackConversion('purchase');
          setStatus('success');
          setPaymentData(data);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attemptsRef.current < maxAttempts) {
          attemptsRef.current += 1;
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (!isMounted) return;
        
        if (attemptsRef.current < maxAttempts) {
          attemptsRef.current += 1;
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };
    
    checkPaymentStatus();
    
    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  const userType = localStorage.getItem('userType');
  const dashboardUrl = userType === 'contractor' ? '/contractor-dashboard' : '/worker-dashboard';

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 bg-[#2B4C6F] border-4 border-black flex items-center justify-center mx-auto mb-6">
                <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
              </div>
              <h1 className="text-2xl font-black uppercase mb-2">Processing Payment</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">Please wait while we confirm your payment...</p>
              <div className="flex justify-center space-x-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-3 h-3 bg-[#2B4C6F] animate-bounce"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-500 border-4 border-black flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} weight="bold" className="text-white" />
              </div>
              <h1 className="text-2xl font-black uppercase mb-2">Payment Successful!</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Thank you for your purchase. Your {paymentData?.product_name} is now active.
              </p>
              
              {paymentData && (
                <div className="bg-neutral-100 dark:bg-neutral-800 border-2 border-black p-4 mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-neutral-600 dark:text-neutral-400">Product:</span>
                    <span className="font-bold">{paymentData.product_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Amount:</span>
                    <span className="font-bold text-emerald-600">${paymentData.amount?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <Button 
                className="w-full h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
                onClick={() => navigate(dashboardUrl)}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 bg-amber-500 border-4 border-black flex items-center justify-center mx-auto mb-6">
                <Clock size={48} weight="bold" className="text-white" />
              </div>
              <h1 className="text-2xl font-black uppercase mb-2">Session Expired</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Button 
                className="w-full h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
                onClick={() => navigate('/pricing')}
              >
                Return to Pricing
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-500 border-4 border-black flex items-center justify-center mx-auto mb-6">
                <XCircle size={48} weight="bold" className="text-white" />
              </div>
              <h1 className="text-2xl font-black uppercase mb-2">
                {status === 'timeout' ? 'Status Check Timeout' : 'Something Went Wrong'}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {status === 'timeout' 
                  ? 'We couldn\'t confirm your payment status. Please check your email for confirmation.'
                  : 'There was an error processing your payment. Please try again or contact support.'
                }
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-[#D95800] hover:bg-[#E55D00] text-white font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
                  onClick={() => navigate('/pricing')}
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  className="w-full h-12 font-bold uppercase border-2 border-black rounded-none"
                  onClick={() => navigate(dashboardUrl)}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
